import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { api, streamAiDraft } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";
import { buildDealTemplatePrefill } from "@/lib/dealTemplatePrefill";
import { buildCoreTradeDocument } from "@/lib/coreTradeDocument";
import { Copy, Sparkles, Plus, X, Square, Link2, ShieldAlert, AlertTriangle, Save, FileDown, PencilLine, Archive } from "lucide-react";
import { toast } from "sonner";
import { CONVERSION_LOCALES } from "@/locales/conversion";
import { LocalizedScope, useWorkspaceTranslation } from "@/locales/workspaceExtras";

const MODES = [
  { key: "create",        labelKey: "st_mode_create",    testid: TESTIDS.studioModeCreate,    sourceNeeded: false, helpKey: "st_help_create" },
  { key: "review",        labelKey: "st_mode_review",    testid: TESTIDS.studioModeReview,    sourceNeeded: true,  helpKey: "st_help_review" },
  { key: "improve",       labelKey: "st_mode_improve",   testid: TESTIDS.studioModeImprove,   sourceNeeded: true,  helpKey: "st_help_improve" },
  { key: "translate",     labelKey: "st_mode_translate", testid: TESTIDS.studioModeTranslate, sourceNeeded: true,  helpKey: "st_help_translate" },
  { key: "compare",       labelKey: "st_mode_compare",   testid: TESTIDS.studioModeCompare,   sourceNeeded: true,  helpKey: "st_help_compare" },
  { key: "adapt",         labelKey: "st_mode_adapt",     testid: TESTIDS.studioModeAdapt,     sourceNeeded: true,  helpKey: "st_help_adapt" },
  { key: "prepare_reply", labelKey: "st_mode_reply",     testid: TESTIDS.studioModeReply,              sourceNeeded: true,  helpKey: "st_help_reply" },
];

const RISK_STYLES = {
  low:    { border: "border-emerald-500/30", text: "text-emerald-400" },
  medium: { border: "border-amber-500/40",   text: "text-amber-400" },
  high:   { border: "border-red-500/50",     text: "text-red-400" },
};

const FREE_COPY = {
  en: { title: "Free evaluation", used: "{used} of {limit} free documents used. Saved documents remain available.", limit: "Your three free documents are complete. Choose a plan to continue.", plans: "View plans" },
  it: { title: "Valutazione gratuita", used: "{used} di {limit} documenti gratuiti utilizzati. I documenti salvati restano disponibili.", limit: "Hai completato i tre documenti gratuiti. Scegli un piano per continuare.", plans: "Vedi i piani" },
  de: { title: "Kostenlose Testphase", used: "{used} von {limit} kostenlosen Dokumenten verwendet. Gespeicherte Dokumente bleiben verfügbar.", limit: "Ihre drei kostenlosen Dokumente sind aufgebraucht. Wählen Sie einen Tarif, um fortzufahren.", plans: "Tarife ansehen" },
};

export default function DraftingPage() {
  const { t, lang } = useI18n();
  const { tr } = useWorkspaceTranslation();
  const freeCopy = CONVERSION_LOCALES[lang]?.draftingFree || FREE_COPY[lang] || FREE_COPY.en;
  const location = useLocation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [language, setLanguage] = useState(lang);
  const [mode, setMode] = useState("create");
  const [values, setValues] = useState({});          // {sectionKey_fieldName: value}
  const [customParams, setCustomParams] = useState([]);
  const [sourceText, setSourceText] = useState("");
  const [dealCtx, setDealCtx] = useState(null);
  const [prefilledCount, setPrefilledCount] = useState(0);
  const [draftTitle, setDraftTitle] = useState("");
  const [output, setOutput] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [draftVersion, setDraftVersion] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState("");
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const controllerRef = useRef(null);
  const outputRef = useRef(null);

  const refreshCapabilities = () => api.capabilities().then(setCapabilities).catch(() => setCapabilities({ ai_drafting: false, manual_editing: true, exports: ["docx", "pdf"] }));
  const goToBillingIfLimited = (code) => {
    if (code !== "free_document_limit_reached") return false;
    toast.info(freeCopy.limit);
    navigate("/app/billing");
    return true;
  };

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    refreshCapabilities();
    api.listTemplates().then((tpls) => {
      setTemplates(tpls);
      const preselect = search.get("code");
      setSelected((current) => {
        if (preselect && tpls.find((x) => x.code === preselect)) return preselect;
        if (tpls.length && !current) return tpls[0].code;
        return current;
      });
    });
    const dealId = search.get("deal");
    const draftId = search.get("draft");
    if (draftId) {
      api.getDraft(draftId).then((dr) => {
        setDraftId(dr.id);
        setDraftVersion(dr.version_number || 1);
        setSelected(dr.template_code || "");
        setLanguage(dr.language || lang);
        setDraftTitle(dr.title || "");
        setOutput(dr.output || "");
        setDirty(false);
        if (dr.parameters) {
          // Best-effort: dump params as custom entries so nothing is lost
          const entries = Object.entries(dr.parameters || {}).map(([k, v], i) => ({ _id: `dr_${i}`, key: k, value: String(v) }));
          setCustomParams(entries);
        }
        if (dr.deal_id) api.getDeal(dr.deal_id).then(setDealCtx).catch(() => setDealCtx(null));
      }).catch(() => { /* ignore, keep default */ });
    } else if (dealId) {
      api.getDeal(dealId).then(setDealCtx).catch(() => setDealCtx(null));
    } else {
      setDealCtx(null);
    }
  }, [location.search, lang]);

  const currentTemplate = useMemo(
    () => templates.find((x) => x.code === selected),
    [templates, selected]
  );

  // Reset values when template changes — pre-fill from deal parameters if available.
  useEffect(() => {
    if (!currentTemplate) return;
    const prefill = buildDealTemplatePrefill(currentTemplate, dealCtx);
    setValues(prefill.values);
    setPrefilledCount(prefill.filled);
  }, [currentTemplate, dealCtx]);

  useEffect(() => {
    if (outputRef.current && streaming) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [output, streaming]);

  const upd = (key, v) => setValues((s) => ({ ...s, [key]: v }));
  const addCustom = () => setCustomParams([...customParams, { _id: `c_${Date.now()}`, key: "", value: "" }]);
  const updCustom = (i, f, v) => setCustomParams(customParams.map((p, idx) => idx === i ? { ...p, [f]: v } : p));
  const rmCustom = (i) => setCustomParams(customParams.filter((_, idx) => idx !== i));

  const stop = () => {
    if (controllerRef.current) { controllerRef.current.abort(); controllerRef.current = null; }
    setStreaming(false); setLoading(false);
  };

  const currentMode = MODES.find((m) => m.key === mode) || MODES[0];
  const isSourceRequired = currentMode.sourceNeeded;
  const missingRequired = useMemo(() => {
    if (!currentTemplate || mode !== "create") return [];
    const missing = [];
    (currentTemplate.sections || []).forEach((sec) => {
      (sec.fields || []).forEach((f) => {
        if (f.required && !values[`${sec.key}_${f.name}`]) missing.push(f.label);
      });
    });
    return missing;
  }, [currentTemplate, values, mode]);

  const buildParameters = () => {
    const paramsObj = {};
    if (currentTemplate) {
      (currentTemplate.sections || []).forEach((sec) => {
        (sec.fields || []).forEach((f) => {
          const value = values[`${sec.key}_${f.name}`];
          if (value !== undefined && value !== null && String(value).trim() !== "") paramsObj[f.label] = value;
        });
      });
    }
    customParams.forEach((parameter) => {
      if (parameter.key) paramsObj[parameter.key] = parameter.value;
    });
    return paramsObj;
  };

  const startManual = () => {
    if (capabilities?.access?.can_create === false) {
      goToBillingIfLimited("free_document_limit_reached");
      return;
    }
    const params = buildParameters();
    const title = draftTitle.trim() || currentTemplate?.title || selected || t("st_manual_document");
    const lines = [`# ${title}`, ""];
    if (Object.keys(params).length) {
      lines.push(`## ${t("st_manual_data")}`);
      Object.entries(params).forEach(([key, value]) => lines.push(`- **${key}:** ${value}`));
      lines.push("");
    }
    lines.push(`## ${t("st_manual_body")}`, t("st_manual_placeholder"));
    setOutput(buildCoreTradeDocument(currentTemplate, values, language, draftTitle.trim(), customParams) || lines.join("\n"));
    setDraftId(null);
    setDraftVersion(null);
    setDirty(true);
  };

  const saveDraft = async () => {
    if (!output.trim()) {
      toast.error(t("st_content_required"));
      return;
    }
    setSaving(true);
    try {
      const saved = draftId
        ? await api.updateDraft(draftId, { title: draftTitle.trim() || null, output })
        : await api.createDraft({
            template_code: selected,
            language,
            parameters: buildParameters(),
            output,
            deal_id: dealCtx?.id || null,
            title: draftTitle.trim() || null,
          });
      setDraftId(saved.id);
      setDraftVersion(saved.version_number || 1);
      setOutput(saved.output || output);
      setDirty(false);
      refreshCapabilities();
      toast.success(t("st_saved"));
    } catch (error) {
      if (goToBillingIfLimited(error?.response?.data?.detail?.code)) return;
      const message = error?.response?.data?.detail;
      toast.error(typeof message === "string" ? message : t("st_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const exportSaved = async (format) => {
    if (!draftId || dirty) return;
    try {
      await api.exportDraft(draftId, format);
      toast.success(t("st_exported").replace("{format}", format.toUpperCase()));
    } catch {
      toast.error(t("st_export_failed"));
    }
  };

  const archiveSaved = async (format) => {
    if (!draftId || dirty || archiving) return;
    setArchiving(format);
    try {
      const file = await api.saveDraftExport(draftId, format);
      toast.success(t("st_archived").replace("{format}", format.toUpperCase()).replace("{version}", file.export_version || 1));
    } catch (error) {
      const code = error?.response?.data?.detail?.code;
      toast.error(code === "immutable_file" ? t("st_immutable") : t("st_archive_failed"));
    } finally { setArchiving(""); }
  };

  const generate = () => {
    if (!selected) return;
    if (capabilities?.access?.can_create === false) {
      goToBillingIfLimited("free_document_limit_reached");
      return;
    }
    if (isSourceRequired && !sourceText.trim()) {
      toast.error(t("st_source_required_toast")); return;
    }
    setLoading(true); setStreaming(true); setOutput(""); setCopied(false);
    setDraftId(null); setDraftVersion(null); setDirty(false);

    const paramsObj = buildParameters();

    controllerRef.current = streamAiDraft(
      {
        template_code: selected,
        parameters: paramsObj,
        language,
        mode,
        source_text: isSourceRequired ? sourceText : null,
        deal_id: dealCtx?.id || null,
        title: draftTitle.trim() || null,
      },
      {
        onStart: (data) => { setDraftId(data.id); setDraftVersion(data.version_number || 1); },
        onToken: (tok) => setOutput((prev) => prev + tok),
        onDone: (data) => { setDraftId(data.id); setDraftVersion(data.version_number || 1); setDirty(false); setStreaming(false); setLoading(false); controllerRef.current = null; refreshCapabilities(); toast.success(t("st_draft_ready")); },
        onError: (err) => { setStreaming(false); setLoading(false); controllerRef.current = null; if (!goToBillingIfLimited(err?.code)) toast.error(err?.message?.slice(0, 200) || t("st_draft_failed")); },
      }
    );
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true); toast.success(t("draft_copied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const risk = currentTemplate?.risk_level || "low";
  const riskStyle = RISK_STYLES[risk];

  return (
    <LocalizedScope><div className="td-document-studio min-w-0 px-4 py-6 sm:p-6 md:p-10 xl:p-12">
      <div className="mb-8">
        <div className="kicker mb-3">— {t("d_nav_studio")}</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-2">{t("d_nav_studio")}</h1>
        <p className="text-[var(--td-text-2)] max-w-2xl">{t("st_intro")}</p>
        {capabilities?.ai_drafting === false && (
          <div className="mt-4 max-w-3xl border border-amber-500/30 bg-amber-500/5 text-amber-200 p-3 text-xs flex items-start gap-2">
            <ShieldAlert size={15} className="shrink-0 mt-0.5" />
            <span>{t("st_ai_unavailable")}</span>
          </div>
        )}
        {capabilities?.access?.enforced && capabilities.access.mode !== "full" && (
          <div className={`mt-4 max-w-3xl border p-4 text-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${capabilities.access.can_create ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
            <div>
              <div className="font-mono uppercase tracking-widest text-[10px] text-[var(--td-gold)] mb-1">{freeCopy.title}</div>
              <span>{freeCopy.used.replace("{used}", capabilities.access.free_documents_used).replace("{limit}", capabilities.access.free_document_limit)}</span>
            </div>
            <Link to="/app/billing" className="btn-ghost !py-2 !px-3 whitespace-nowrap">{freeCopy.plans}</Link>
          </div>
        )}
        {dealCtx && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Link to={`/app/deals/${dealCtx.id}`}
                  className="inline-flex items-center gap-2 border border-[var(--td-gold)] text-[var(--td-gold)] px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-[var(--td-gold-muted)]">
              <Link2 size={12} /> {t("dd_context")}: {dealCtx.title}
            </Link>
            {prefilledCount > 0 && (
              <span data-testid="deal-prefill-count" className="text-xs text-emerald-300">
                {t("st_prefilled_from_deal").replace("{count}", prefilledCount)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Mode strip */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-6" data-testid={TESTIDS.studioMode}>
        {MODES.map((m) => (
          <button key={m.key} data-testid={m.testid} onClick={() => setMode(m.key)}
                  className={`px-3 py-2 border font-mono text-[11px] uppercase tracking-widest min-w-0 min-h-[44px] whitespace-normal transition-colors ${
                    mode === m.key
                      ? "border-[var(--td-gold)] text-[var(--td-gold)] bg-[rgba(212,175,55,0.06)]"
                      : "border-[var(--td-border)] text-[var(--td-text-2)] hover:text-[var(--td-text)]"
                  }`}>
            {t(m.labelKey)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-5 2xl:gap-8">
        {/* LEFT — form */}
        <div className="card-surface p-4 sm:p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <label className="td-label">{t("draft_template")}</label>
              <select data-testid={TESTIDS.draftTemplateSelect} className="td-input" value={selected}
                      onChange={(e) => setSelected(e.target.value)}>
                {templates.map((tp) => (<option key={tp.id} value={tp.code}>{tp.code} — {tr(tp.title)}</option>))}
              </select>
            </div>
            <div>
              <label className="td-label">{t("draft_language")}</label>
              <select data-testid={TESTIDS.draftLanguageSelect} className="td-input" value={language}
                      onChange={(e) => setLanguage(e.target.value)}>
                <option value="en">English</option>
                <option value="it">Italiano</option>
                <option value="de">Deutsch</option>
                <option value="es">Spanish</option>
                <option value="zh">Chinese</option>
                <option value="ru">Russian</option>
              </select>
            </div>
          </div>

          {currentTemplate && (
            <div className="flex items-center gap-3 -mt-1">
              <span data-testid={TESTIDS.studioRiskBadge} className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${riskStyle.border} ${riskStyle.text}`}>
                {t("st_risk_prefix")}: {t(`st_risk_${risk}`)}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">
                {currentTemplate.category}
              </span>
            </div>
          )}

          <div data-testid={TESTIDS.studioModeHelp} className="p-3 rounded border border-[var(--td-border)] bg-[var(--td-bg)] text-xs text-[var(--td-text-2)]">
            {t(currentMode.helpKey)}
          </div>

          <div>
            <label className="td-label">{t("st_doc_title")}</label>
            <input data-testid="draft-title-input" className="td-input"
                   placeholder={t("st_doc_title_ph")}
                   value={draftTitle} onChange={(e) => { setDraftTitle(e.target.value); if (output) setDirty(true); }} />
          </div>

          {isSourceRequired && (
            <div>
              <label className="td-label">{t("st_source_label")}</label>
              <textarea data-testid={TESTIDS.studioSourceText} rows={8}
                        className="td-input resize-none font-mono text-xs"
                        placeholder={t("st_source_ph")}
                        value={sourceText} onChange={(e) => setSourceText(e.target.value)} />
            </div>
          )}

          {/* Schema-driven fields */}
          {currentTemplate && (currentTemplate.sections || []).length > 0 && (
            <div className="space-y-4">
              {currentTemplate.sections.map((sec) => (
                <div key={sec.key}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-2">{tr(sec.title)}</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {sec.fields.map((f) => (
                      <SchemaField key={f.name} f={f} sectionKey={sec.key}
                                   value={values[`${sec.key}_${f.name}`] || ""}
                                   onChange={(v) => upd(`${sec.key}_${f.name}`, v)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Custom params */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="td-label !mb-0">{tr("Extra parameters (optional)")}</label>
              <button type="button" onClick={addCustom} data-testid={TESTIDS.draftAddParam}
                      className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] flex items-center gap-1">
                <Plus size={12} /> {t("draft_add_param")}
              </button>
            </div>
            <div className="space-y-2">
              {customParams.map((p, i) => (
                <div key={p._id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_44px] gap-2">
                  <input className="td-input !py-1.5 !text-xs font-mono" placeholder={tr("key")}
                         value={p.key} onChange={(e) => updCustom(i, "key", e.target.value)} />
                  <input className="td-input !py-1.5 !text-xs" placeholder={tr("value")}
                         value={p.value} onChange={(e) => updCustom(i, "value", e.target.value)} />
                  <button onClick={() => rmCustom(i)} className="text-[var(--td-text-3)] hover:text-red-400 min-h-[44px] flex items-center justify-center"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {mode === "create" && missingRequired.length > 0 && (
            <div data-testid={TESTIDS.studioMissingRequired} className="p-3 border border-amber-500/30 bg-amber-500/5 text-xs text-amber-300 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-mono uppercase tracking-widest text-[10px] mb-1">{t("st_missing_required")}</div>
                <div>{missingRequired.slice(0, 6).join(" · ")}</div>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button" onClick={startManual} disabled={!selected || loading}
                    data-testid={TESTIDS.draftManualStart}
                    className="btn-ghost w-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <PencilLine size={14} /> {t("st_start_manual")}
            </button>
            <button type="button" onClick={loading ? stop : generate}
                    disabled={!selected || (!loading && capabilities?.ai_drafting === false)}
                    data-testid={TESTIDS.draftGenerate}
                    className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {loading
                ? <><Square size={12} fill="currentColor" /> {t("st_stop")}</>
                : <><Sparkles size={14} /> {t("draft_generate")}</>}
            </button>
          </div>
        </div>

        {/* RIGHT — output */}
        <div className="card-surface p-4 sm:p-6 flex flex-col min-h-[420px] sm:min-h-[560px]">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex flex-wrap min-w-0 items-center gap-3">
              <label className="td-label !mb-0">{t("draft_output")}</label>
              {draftVersion && <span className="font-mono text-[9px] uppercase tracking-widest border border-[var(--td-border)] px-1.5 py-0.5">v{draftVersion}</span>}
              {dirty && <span className="font-mono text-[9px] uppercase tracking-widest text-amber-300">{t("st_unsaved")}</span>}
              {streaming && (
                <div className="flex items-center gap-1.5" data-testid="draft-streaming-indicator">
                  <div className="w-1.5 h-1.5 rounded-full bg-[var(--td-gold)] animate-pulse" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)]">{t("draft_generating")}</span>
                </div>
              )}
            </div>
            {output && !streaming && <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={copy} data-testid={TESTIDS.draftCopy}
                      className="btn-ghost !py-1.5 !px-2.5 font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1">
                <Copy size={12} /> {copied ? t("draft_copied") : t("draft_copy")}
              </button>
              <button type="button" onClick={saveDraft} disabled={saving || !dirty && !!draftId} data-testid={TESTIDS.draftSave}
                      className="btn-ghost !py-1.5 !px-2.5 font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <Save size={12} /> {saving ? t("st_saving") : t("st_save")}
              </button>
              <button type="button" onClick={() => exportSaved("docx")} disabled={!draftId || dirty} data-testid={TESTIDS.draftExportDocx}
                      className="btn-ghost !py-1.5 !px-2.5 font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <FileDown size={12} /> Word
              </button>
              <button type="button" onClick={() => exportSaved("pdf")} disabled={!draftId || dirty} data-testid={TESTIDS.draftExportPdf}
                      className="btn-ghost !py-1.5 !px-2.5 font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <FileDown size={12} /> PDF
              </button>
              <button type="button" onClick={() => archiveSaved("docx")} disabled={!draftId || dirty || !!archiving} data-testid="draft-archive-docx"
                      className="btn-ghost !py-1.5 !px-2.5 font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <Archive size={12} /> {archiving === "docx" ? t("st_archiving") : t("st_archive_word")}
              </button>
              <button type="button" onClick={() => archiveSaved("pdf")} disabled={!draftId || dirty || !!archiving} data-testid="draft-archive-pdf"
                      className="btn-ghost !py-1.5 !px-2.5 font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <Archive size={12} /> {archiving === "pdf" ? t("st_archiving") : t("st_archive_pdf")}
              </button>
            </div>}
          </div>
          <textarea ref={outputRef} data-testid={TESTIDS.draftOutput} value={output}
                    readOnly={streaming} onChange={(event) => { setOutput(event.target.value); setDirty(true); }}
                    placeholder={loading ? t("draft_generating") : t("st_editor_placeholder")}
                    className="flex-1 min-h-[320px] sm:min-h-[470px] resize-none bg-[var(--td-bg)] border border-[var(--td-border)] p-3 sm:p-5 font-mono text-xs leading-relaxed text-[var(--td-text)] overflow-y-auto focus:outline-none focus:border-[var(--td-gold)]" />
          {dirty && draftId && <p className="mt-2 text-[10px] text-amber-300">{t("st_save_before_export")}</p>}
        </div>
      </div>
    </div></LocalizedScope>
  );
}

function SchemaField({ f, sectionKey, value, onChange }) {
  const { tr } = useWorkspaceTranslation();
  const tid = `${TESTIDS.studioSchemaField}.${sectionKey}.${f.name}`;
  const isBig = f.type === "textarea";
  const wrapper = isBig ? "sm:col-span-2" : "";
  return (
    <div className={wrapper}>
      <label className="td-label">
        {tr(f.label)}{f.required && <span className="text-[var(--td-gold)]"> *</span>}
      </label>
      {f.type === "select" ? (
        <select data-testid={tid} className="td-input !py-1.5 !text-xs"
                value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{tr(o)}</option>)}
        </select>
      ) : f.type === "textarea" ? (
        <textarea data-testid={tid} rows={2} placeholder={tr(f.placeholder || "")}
                  className="td-input !py-1.5 !text-xs resize-none"
                  value={value} onChange={(e) => onChange(e.target.value)} />
      ) : f.type === "date" ? (
        <input data-testid={tid} type="date" className="td-input !py-1.5 !text-xs"
               value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={tid} className="td-input !py-1.5 !text-xs" placeholder={tr(f.placeholder || "")}
               value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {f.help_text && <div className="text-[10px] text-[var(--td-text-3)] mt-0.5">{tr(f.help_text)}</div>}
    </div>
  );
}
